/**
 * Aspect tables of the chart view: legend, grid and the list of aspects.
 * Moved from screens/me/NatalChart.tsx; `AspectsTable` is renamed `ChartAspectsTable`
 * (components/onboarding/AspectsTable.tsx is a different component).
 */

import { useTranslation } from 'react-i18next';
import type { NatalSelectKind } from './NatalChartSvg';
import type { ChartPlanet, ChartAspect } from '../../hooks/useChart';
import { ASPECT_GLOSSARY } from './natalGlossary';
import styles from '../../screens/me/MeSubpages.module.css';
import { PLANET_GLYPH, ASPECT_GLYPH, fmtDeg, planetLabel } from './chartViewHelpers';

const ASPECT_ORDER = ['conjunction', 'sextile', 'square', 'trine', 'opposition', 'quincunx', 'semisextile'];

export function AspectLegendTable({ onPickType }: { onPickType: (type: string) => void }): JSX.Element {
  const { t } = useTranslation('me');
  return (
    <section className={styles.tableCard} aria-labelledby="aspect-legend-h">
      <h2 id="aspect-legend-h" className={styles.tableHeader}>{t('natalChart.chrome.aspectLegendHeader')}</h2>
      <table className={styles.table} data-testid="natal-aspect-legend-table">
        <thead>
          <tr>
            <th>{t('natalChart.chrome.colAspect')}</th>
            <th style={{ textAlign: 'right' }}>{t('natalChart.chrome.colAngle')}</th>
            <th>{t('natalChart.chrome.colNature')}</th>
          </tr>
        </thead>
        <tbody>
          {ASPECT_ORDER.map((type) => {
            const a = ASPECT_GLOSSARY[type];
            if (!a) return null;
            return (
              <tr key={type}>
                <td>
                  <button type="button" className={`${styles.linkCell} ${styles.linkCellWrap}`} onClick={() => onPickType(type)}>
                    <span className={styles.tableGlyph} aria-hidden="true">{a.glyph}</span>
                    {t(`natalChart.glossary.aspect.${type}.title`, { defaultValue: a.title })}
                    {!a.major && <span className={styles.minorFlag}>{t('natalChart.chrome.minorFlag')}</span>}
                  </button>
                </td>
                <td className={styles.tableNum}>{a.angle}°</td>
                <td>
                  <span className={`${styles.natureChip} ${styles[`nature_${a.nature}`]}`}>
                    {t(`natalChart.names.natures.${a.nature}`, { defaultValue: a.nature })}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

export function AspectGrid({ planets, aspects, onPickAspect }: {
  planets: ChartPlanet[];
  aspects: ChartAspect[];
  onPickAspect: (type: string, extra: { from: string; to: string; orb: number }) => void;
}): JSX.Element {
  const { t } = useTranslation('me');
  // Lookup: pair key (sorted by index) → first matching aspect
  const lookup = new Map<string, ChartAspect>();
  const indexOf = new Map<string, number>(planets.map((p, i) => [p.id, i]));
  for (const a of aspects) {
    const i1 = indexOf.get(a.planet1);
    const i2 = indexOf.get(a.planet2);
    if (i1 === undefined || i2 === undefined) continue;
    const [lo, hi] = i1 < i2 ? [i1, i2] : [i2, i1];
    const key = `${lo}-${hi}`;
    // Prefer major + smaller orb
    const prev = lookup.get(key);
    if (!prev || (a.isMajor && !prev.isMajor) || (a.isMajor === prev.isMajor && a.orb < prev.orb)) {
      lookup.set(key, a);
    }
  }

  return (
    <section className={styles.tableCard} aria-labelledby="aspect-grid-h">
      <h2 id="aspect-grid-h" className={styles.tableHeader}>{t('natalChart.chrome.aspectGridHeader')}</h2>
      <div className={styles.gridScroll}>
        <table className={styles.aspectGrid} data-testid="natal-aspect-grid">
          <thead>
            <tr>
              <th aria-label={t('natalChart.aspectGrid.planetColumnLabel')} />
              {planets.map((p) => (
                <th key={p.id} className={styles.gridHead} title={planetLabel(p.id, t)}>
                  {PLANET_GLYPH[p.id] ?? '·'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {planets.map((row, ri) => (
              <tr key={row.id}>
                <th scope="row" className={styles.gridHead} title={planetLabel(row.id, t)}>
                  {PLANET_GLYPH[row.id] ?? '·'}
                </th>
                {planets.map((col, ci) => {
                  if (ci >= ri) return <td key={col.id} className={styles.gridCellEmpty} />;
                  const key = `${ci}-${ri}`;
                  const a = lookup.get(key);
                  if (!a) return <td key={col.id} className={styles.gridCellEmpty}>·</td>;
                  const glyph = ASPECT_GLOSSARY[a.type]?.glyph ?? '·';
                  const nature = ASPECT_GLOSSARY[a.type]?.nature ?? 'neutral';
                  return (
                    <td key={col.id} className={styles.gridCell}>
                      <button
                        type="button"
                        className={`${styles.gridBtn} ${styles[`nature_${nature}`]}`}
                        onClick={() => onPickAspect(a.type, { from: a.planet1, to: a.planet2, orb: a.orb })}
                        title={`${planetLabel(a.planet1, t)} ${t(`natalChart.names.aspects.${a.type}`, { defaultValue: a.type })} ${planetLabel(a.planet2, t)} (${t('natalChart.chrome.colOrb')} ${a.orb.toFixed(1)}°)`}
                      >
                        {glyph}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ChartAspectsTable({ aspects, onPick, onPickAspect }: {
  aspects: ChartAspect[];
  onPick: (kind: NatalSelectKind, id: string) => void;
  onPickAspect: (type: string, extra: { from: string; to: string; orb: number }) => void;
}): JSX.Element {
  const { t } = useTranslation('me');
  const major = aspects.filter((a) => a.isMajor);
  const list = major.length > 0 ? major : aspects;
  return (
    <section className={styles.tableCard} aria-labelledby="aspects-h">
      <h2 id="aspects-h" className={styles.tableHeader}>{t('natalChart.chrome.aspectsHeader')}{major.length > 0 ? ` ${t('natalChart.chrome.majorSuffix')}` : ''}</h2>
      <table className={styles.table} data-testid="natal-aspects-table">
        <thead>
          <tr><th>{t('natalChart.chrome.colFrom')}</th><th>{t('natalChart.chrome.colAspect')}</th><th>{t('natalChart.chrome.colTo')}</th><th style={{ textAlign: 'right' }}>{t('natalChart.chrome.colOrb')}</th></tr>
        </thead>
        <tbody>
          {list.map((a, i) => (
            <tr key={`${a.planet1}-${a.planet2}-${a.type}-${i}`}>
              <td>
                <button type="button" className={styles.linkCell} onClick={() => onPick('planet', a.planet1)}>
                  <span className={styles.tableGlyph} aria-hidden="true">{PLANET_GLYPH[a.planet1] ?? '·'}</span>
                  {planetLabel(a.planet1, t)}
                </button>
              </td>
              <td>
                <button
                  type="button"
                  className={styles.linkCell}
                  onClick={() => onPickAspect(a.type, { from: a.planet1, to: a.planet2, orb: a.orb })}
                >
                  <span className={styles.tableGlyph} aria-hidden="true">{ASPECT_GLYPH[a.type] ?? '·'}</span>
                  <span className={styles.tableSign}>{t(`natalChart.names.aspects.${a.type}`, { defaultValue: a.type })}</span>
                </button>
              </td>
              <td>
                <button type="button" className={styles.linkCell} onClick={() => onPick('planet', a.planet2)}>
                  <span className={styles.tableGlyph} aria-hidden="true">{PLANET_GLYPH[a.planet2] ?? '·'}</span>
                  {planetLabel(a.planet2, t)}
                </button>
              </td>
              <td className={styles.tableNum}>{fmtDeg(a.orb)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
